import * as React from "react";

import { EyeClosedIcon, EyeIcon } from "lucide-react";
import { useState } from "react";
import { cn } from "../../lib/utils";
import {
  FormControl,
  FormLabel,
  FormDescription,
  FormMessage,
  FormItem,
  FormItemProps,
} from "../form";
import { FormCurrencyInput, CurrencyInput } from "./currency-input";
import { SearchInput } from "./search-input";

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full px-3 py-2 text-base md:text-sm",
          "bg-background placeholder:text-muted-foreground",
          "rounded-md border border-input",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", // focus-visible:ring-offset-2
          type === "number" &&
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

const FormInput = React.forwardRef<
  HTMLInputElement,
  FormItemProps<typeof Input, string> & { withEye?: boolean }
>(({ className, label, description, onChange, withEye, ...props }, ref) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <FormItem className={className}>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        {withEye ? (
          <InputWithEye {...props} ref={ref} onChange={handleChange} />
        ) : (
          <Input
            {...props}
            value={props.value ?? ""}
            ref={ref}
            onChange={handleChange}
          />
        )}
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
});

FormInput.displayName = "FormInput";

export const InputWithEye = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>((props, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const type = showPassword ? "text" : props.type;
  return (
    <div className="relative">
      <Input {...props} ref={ref} type={type} />
      {showPassword ? (
        <EyeClosedIcon
          className="absolute hover:bg-muted rounded-lg p-1 stroke-muted-foreground size-6 right-2 top-1/2 -translate-y-1/2"
          onClick={() => setShowPassword(false)}
        />
      ) : (
        <EyeIcon
          className="absolute hover:bg-muted rounded-lg p-1 stroke-muted-foreground size-6 right-2 top-1/2 -translate-y-1/2"
          onClick={() => setShowPassword(true)}
        />
      )}
    </div>
  );
});

InputWithEye.displayName = "InputWithEye";

export { Input, FormInput, FormCurrencyInput, CurrencyInput, SearchInput };
